'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, Tooltip } from '~/components/primitives';
import useWishlistState from './useWishlistState';
import useWishlistActions from './useWishlistActions';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '~/components/ui/hover-card';
import { HeaderHovercard, HeaderHovercardTitle, HeaderHovercardList, HeaderHovercardCTA, HeaderHovercardEmpty, HeaderHovercardItem, HeaderHovercardItemImage } from '~/features/cart/ui/header-hovercard';
import { useI18n } from '~/features/i18n';
import { usePortalCtx, usePortalHREF_relative, usePortalHREFs } from '~/features/global/portal';
import { wishlistHovercardEvent } from '~/features/cart/cartHovercardEvents';
import { useRouter } from 'next/navigation';
import { Wishlist_ShareLinkDialog_Mount, useWishlist_ShareLinkDialog_OpenAction } from './ShareWishlistDialog';
import { PORTAL_RELATIVE_HREF_CART, PORTAL_RELATIVE_HREF_WISHLIST } from '../cart/hooks';
const AUTOCLOSE_DELAY = 3000;
const Wishlist_NavbarButton: React.FC = _props => {
  // Compiled, this component renders fewer hooks on the client than it did on the first render
  // and takes the homepage down with it. The rules-of-hooks lint finds nothing in it or the
  // hooks it calls, so it is opted out until the compiled output is understood.
  'use no memo';

  const wishlist = useWishlistState();
  const i18n = useI18n();
  const portal = usePortalCtx();
  const hrefs = usePortalHREFs();
  const router = useRouter();
  const actions = useWishlistActions();
  const count = wishlist.items.length;
  const [open, setOpen] = useState(false);
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const WISHLIST_HREF = usePortalHREF_relative(PORTAL_RELATIVE_HREF_WISHLIST);
  const CART_HREF = usePortalHREF_relative(PORTAL_RELATIVE_HREF_CART);
  const clearAutoClose = useCallback(() => {
    if (autoCloseRef.current) {
      clearTimeout(autoCloseRef.current);
      autoCloseRef.current = null;
    }
  }, []);
  useEffect(() => {
    return wishlistHovercardEvent.subscribe(() => {
      setOpen(true);
      clearAutoClose();
      autoCloseRef.current = setTimeout(() => {
        setOpen(false);
      }, AUTOCLOSE_DELAY);
    });
  }, [clearAutoClose]);
  useEffect(() => () => clearAutoClose(), [clearAutoClose]);
  const openShareWishlistDialog = useWishlist_ShareLinkDialog_OpenAction();
  return <HoverCard open={open} onOpenChange={nextOpen => {
    setOpen(nextOpen);
    if (nextOpen) clearAutoClose();
  }} openDelay={200} closeDelay={150}>
            <HoverCardTrigger asChild>
                <Link href={WISHLIST_HREF} className="relative block" aria-label={i18n._('wishlist.header.aria', undefined, {
        message: 'Wishlist'
      })}>
                    {count > 0 && <div className="absolute -top-1 left-4">
                            <p className="flex h-1 w-1 items-center justify-center rounded-full bg-orange p-2 text-xs font-bold text-white">
                                {count}
                            </p>
                        </div>}
                    <Icon name="heart" />
                </Link>
            </HoverCardTrigger>

            <HoverCardContent align="center" sideOffset={16} showArrow className="w-80 origin-top">
                {count > 0 ? <HeaderHovercard>
                        <div className="mb-3 flex items-center justify-between">
                            <HeaderHovercardTitle href={WISHLIST_HREF} className="mb-0">
                                {i18n._('wishlist.header.title', undefined, {
              message: 'Wishlist'
            })}
                            </HeaderHovercardTitle>
                            <div className="flex items-center gap-1">
                                <Tooltip content={i18n._('wishlist.share', undefined, {
              message: 'Share wishlist'
            })}>
                                    <button type="button" className="rounded-md p-1.5 text-blue-400 transition-colors hover:bg-blue-50 hover:text-primary" onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                openShareWishlistDialog();
              }}>
                                        <Icon name="share" size="xs" />
                                    </button>
                                </Tooltip>
                                <Tooltip content={actions.allReady ? i18n._('wishlist.move-all', undefined, {
              message: 'Move all to cart'
            }) : i18n._('wishlist.tooltip.all-incomplete', undefined, {
              message: 'Select a variant and language for every activity'
            })}>
                                    <button type="button" className="rounded-md p-1.5 text-blue-400 transition-colors hover:bg-blue-50 hover:text-primary disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-blue-400" disabled={actions.isMoving} onClick={async e => {
                e.preventDefault();
                e.stopPropagation();
                if (!actions.allReady) {
                  setOpen(false);
                  router.push(WISHLIST_HREF);
                  return;
                }
                await actions.moveAllReadyToCart();
                setOpen(false);
                router.push(CART_HREF);
              }}>
                                        <Icon name="bag" size="xs" />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                        <HeaderHovercardList>
                            {[...wishlist.items].reverse().slice(0, 5).map(item => {
            const activityHref = item.slug ? `${hrefs.PDP({
              slug: item.slug,
              id: item.activityId
            })}&nomodal` : null;
            const addedAtLabel = new Intl.DateTimeFormat(i18n.locale, {
              dateStyle: 'short',
              timeStyle: 'short'
            }).format(new Date(item.addedAt));
            const isUnavailable = portal.portal === 'BIZZ' && portal.bizz.excludedActivityIDs.includes(item.activityId);
            return <li key={item.activityId} className="first:pt-0">
                                            <HeaderHovercardItem href={activityHref}>
                                                <HeaderHovercardItemImage src={item.imagePath} alt={item.name || 'Wishlist activity'} className={isUnavailable ? 'opacity-50 grayscale' : undefined} />
                                                <div className="min-w-0">
                                                    <p className="truncate text-xs font-semibold text-primary">
                                                        {item.name || `#${item.activityId}`}
                                                    </p>
                                                    {isUnavailable ? <p className="mt-1 truncate text-[11px] text-orange-500">
                                                            {i18n._('wishlist.item.unavailable-for-bizz_{bizzName}', {
                      bizzName: portal.portal === 'BIZZ' ? portal.bizz.name : ''
                    }, {
                      message: 'This activity is not available for {bizzName}'
                    })}
                                                        </p> : <p className="mt-1 text-[11px] text-blue-500">
                                                            {i18n._('wishlist.item.added-on_{date}', {
                      date: addedAtLabel
                    }, {
                      message: 'Added on {date}'
                    })}
                                                        </p>}
                                                </div>
                                                <button type="button" className="ml-auto shrink-0 p-1 text-blue-400 transition-colors hover:text-orange-500" aria-label={i18n._('wishlist.item.remove.aria', undefined, {
                  message: 'Remove from wishlist'
                })} onClick={event_0 => {
                  event_0.preventDefault();
                  event_0.stopPropagation();
                  wishlist.remove(item.activityId);
                }}>
                                                    <Icon name="trash" size="xs" />
                                                </button>
                                            </HeaderHovercardItem>
                                        </li>;
          })}
                        </HeaderHovercardList>
                        <HeaderHovercardCTA href={WISHLIST_HREF}>
                            {i18n._('wishlist.header.view', undefined, {
            message: 'View wishlist'
          })}
                        </HeaderHovercardCTA>
                    </HeaderHovercard> : <HeaderHovercardEmpty icon="heart" message={i18n._('wishlist.empty.title', undefined, {
        message: 'Your wishlist is empty'
      })} ctaLabel={i18n._('wishlist.empty.cta', undefined, {
        message: 'Start adding teambuildings'
      })} ctaHref={hrefs.PLP()} />}
            </HoverCardContent>

            <Wishlist_ShareLinkDialog_Mount />
        </HoverCard>;
};
export default Wishlist_NavbarButton;